import { qs, qsa } from '../utils/dom';
import { DURATION, EASE_OUT, reducedMotion } from '../utils/motion';

// Matches the `lg` breakpoint in the stylesheet: above it the sub-menus are
// drop-downs, below it they fold open inside the panel.
const DESKTOP = '(min-width: 1024px)';

// Hover intent. Opening waits a moment so a cursor crossing the bar does not
// flash every drop-down on its way; closing waits longer, because the trip from
// the parent item down to the panel passes over dead space and a menu that shuts
// mid-way is the classic reason people give up on drop-downs.
const OPEN_DELAY = 70;
const CLOSE_DELAY = 220;

/**
 * Sub-menus.
 *
 * The open state is one class on the item — `.is-open` — whoever set it: hover,
 * focus, click or touch. CSS animates the drop-down on desktop; inside the
 * mobile panel the fold is animated here, because a height of `auto` cannot be
 * transitioned.
 */
export const initSubmenus = () => {
  const nav = qs('.site-nav');

  if (!nav) {
    return;
  }

  // Announced before anything else: the stylesheet only collapses the folded
  // sub-menus while this class is present, so a page whose script never arrives
  // shows every level instead of hiding them behind a fold that cannot open.
  document.documentElement.classList.add('has-menu-js');

  const desktop = window.matchMedia(DESKTOP);
  const parents = qsa('.menu__item.has-children', nav);
  const timers = new WeakMap();
  const animations = new WeakMap();

  const subOf = (item) => item.querySelector(':scope > .menu__sub');
  const toggleOf = (item) => item.querySelector(':scope > .menu__toggle');

  const cancel = (item) => {
    window.clearTimeout(timers.get(item));
    timers.delete(item);
  };

  /** Folds a sub-menu open or shut inside the mobile panel. */
  const fold = (item, open) => {
    const sub = subOf(item);

    if (!sub) {
      return;
    }

    animations.get(sub)?.cancel();

    if (reducedMotion()) {
      sub.style.height = open ? 'auto' : '0px';
      return;
    }

    // From wherever it is now, so a fold interrupted mid-way carries on rather
    // than jumping to the start.
    const from = sub.getBoundingClientRect().height;

    sub.style.height = 'auto';
    const to = open ? sub.getBoundingClientRect().height : 0;
    sub.style.height = `${from}px`;

    const animation = sub.animate(
      { height: [`${from}px`, `${to}px`], opacity: [open ? 0 : 1, open ? 1 : 0] },
      { duration: DURATION.base, easing: EASE_OUT }
    );

    animations.set(sub, animation);

    animation.onfinish = () => {
      animations.delete(sub);
      // `auto` again, so a sub-menu that grows later is not clipped.
      sub.style.height = open ? 'auto' : '0px';
      sub.style.opacity = '';
    };
  };

  const setOpen = (item, open) => {
    cancel(item);

    if (item.classList.contains('is-open') === open) {
      return;
    }

    item.classList.toggle('is-open', open);
    toggleOf(item)?.setAttribute('aria-expanded', String(open));

    if (!desktop.matches) {
      fold(item, open);
    }
  };

  /** Shuts everything except the branch the given element sits in. */
  const closeOthers = (except) => {
    parents.forEach((item) => {
      if (except && (item === except || item.contains(except))) {
        return;
      }

      setOpen(item, false);
    });
  };

  const schedule = (item, open) => {
    cancel(item);
    timers.set(
      item,
      window.setTimeout(() => {
        if (open) {
          closeOthers(item);
        }

        setOpen(item, open);
      }, open ? OPEN_DELAY : CLOSE_DELAY)
    );
  };

  parents.forEach((item) => {
    // Pointer. A touch reports as a pointerenter too, so touch is left to the
    // button below — otherwise the first tap would open and follow the link.
    item.addEventListener('pointerenter', (event) => {
      if (desktop.matches && 'mouse' === event.pointerType) {
        schedule(item, true);
      }
    });

    item.addEventListener('pointerleave', (event) => {
      if (desktop.matches && 'mouse' === event.pointerType) {
        schedule(item, false);
      }
    });

    // Keyboard: the branch stays open while the focus is inside it.
    item.addEventListener('focusin', () => {
      if (desktop.matches) {
        cancel(item);
        closeOthers(item);
        setOpen(item, true);
      }
    });

    item.addEventListener('focusout', (event) => {
      if (desktop.matches && !item.contains(event.relatedTarget)) {
        setOpen(item, false);
      }
    });
  });

  // The button is what makes a sub-menu reachable by touch and by keyboard, and
  // the only thing assistive tech can read the state from.
  nav.addEventListener('click', (event) => {
    const toggle = event.target.closest('.menu__toggle');

    if (!toggle || !nav.contains(toggle)) {
      return;
    }

    event.preventDefault();

    const item = toggle.closest('.menu__item');
    const open = !item.classList.contains('is-open');

    if (open) {
      closeOthers(item);
    }

    setOpen(item, open);
  });

  document.addEventListener('click', (event) => {
    if (!nav.contains(event.target)) {
      closeOthers(null);
    }
  });

  document.addEventListener('keydown', (event) => {
    if ('Escape' !== event.key) {
      return;
    }

    const open = nav.querySelector('.menu__item.is-open');

    if (!open) {
      return;
    }

    closeOthers(null);
    // Focus goes back to the control that opened the branch, not to the top.
    toggleOf(open)?.focus();
  });

  // Crossing the breakpoint leaves the other mode's inline height behind.
  desktop.addEventListener('change', () => {
    parents.forEach((item) => {
      const sub = subOf(item);

      animations.get(sub)?.cancel();

      if (sub) {
        sub.style.height = '';
        sub.style.opacity = '';
      }

      setOpen(item, false);
    });
  });
};

/** Mobile menu: body class drives the CSS, aria-expanded drives assistive tech. */
export const initMenu = () => {
  const burger = qs('[data-burger]');
  const nav = qs('.site-nav');

  if (!burger || !nav) {
    return;
  }

  const setState = (open) => {
    document.body.classList.toggle('is-menu-open', open);
    burger.setAttribute('aria-expanded', String(open));
    // Locking the body prevents the page behind the overlay from scrolling.
    document.body.style.overflow = open ? 'hidden' : '';
  };

  burger.addEventListener('click', () => {
    setState(!document.body.classList.contains('is-menu-open'));
  });

  document.addEventListener('keydown', (event) => {
    if ('Escape' === event.key) {
      setState(false);
    }
  });

  // Closing on navigation matters for same-page anchors, where no reload happens.
  nav.addEventListener('click', (event) => {
    if (event.target.closest('a')) {
      setState(false);
    }
  });
};
